import React, { useState, useRef, useEffect } from "react";
import { useNavigate } from "react-router-dom";
import {
  Mail,
  Lock,
  Eye,
  EyeOff,
  AlertCircle,
  ArrowRight,
  ArrowLeft,
  ShieldCheck,
} from "lucide-react";
import Semi360Logo from "components/Semi360Logo";
import "./LoginPage.css";

const Glyph = ({ className }) => (
  <svg
    className={className}
    viewBox="0 0 200 200"
    xmlns="http://www.w3.org/2000/svg"
    fill="#24D0C0"
    aria-hidden="true"
  >
    <path d="M100 0C44.7 0 0 44.7 0 100s44.7 100 100 100 100-44.7 100-100S155.3 0 100 0Zm0 134.6c-19.1 0-34.6-15.6-34.6-34.6S80.9 65.4 100 65.4s34.6 15.6 34.6 34.6-15.5 34.6-34.6 34.6Zm53.3-34.6c0-29.5-23.9-53.4-53.3-53.4-19.6 0-36.8 10.7-46 26.5 5.6-27.8 30.2-48.7 59.5-48.7 33.5 0 60.7 27.3 60.7 60.9 0 27.4-18.1 50.6-43 58.2 13.4-9.7 22.1-25.5 22.1-43.5Zm-53.3 81.7c-44.7 0-81.1-36.5-81.1-81.7 0-27.2 13.4-51.3 33.8-66.1-11.6 13.9-18.6 31.7-18.6 51.2 0 44.1 35.5 79.7 79.4 79.7 22.2 0 42.4-9.2 56.7-23.9-14.1 24.2-40.3 40.8-70.2 40.8Z" />
  </svg>
);

const EMAIL_RE = /^[^\s@]+@[^\s@]+\.[^\s@]{2,}$/;
const validateEmail = (v) => {
  const t = (v || "").trim();
  if (!t) return "Enter your work email.";
  if (!EMAIL_RE.test(t)) return "That doesn't look like a valid email.";
  return "";
};

const OtpInput = ({ otp, setOtp, error, refs }) => {
  const setAt = (i, ch) => setOtp((a) => a.map((v, j) => (j === i ? ch : v)));
  const onChange = (i, e) => {
    const d = e.target.value.replace(/\D/g, "");
    if (!d) return setAt(i, "");
    setAt(i, d[d.length - 1]);
    if (i < 5) refs.current[i + 1]?.focus();
  };
  const onKeyDown = (i, e) => {
    if (e.key === "Backspace" && !otp[i] && i > 0) {
      refs.current[i - 1]?.focus();
      setAt(i - 1, "");
    }
  };
  const onPaste = (e) => {
    e.preventDefault();
    const d = (e.clipboardData.getData("text") || "").replace(/\D/g, "").slice(0, 6);
    if (!d) return;
    const arr = ["", "", "", "", "", ""];
    for (let j = 0; j < d.length; j++) arr[j] = d[j];
    setOtp(arr);
    refs.current[Math.min(d.length, 5)]?.focus();
  };
  return (
    <div className="sb-field">
      <div className="flex gap-2 justify-between" onPaste={onPaste}>
        {otp.map((v, i) => (
          <input
            key={i}
            ref={(el) => (refs.current[i] = el)}
            type="text"
            inputMode="numeric"
            maxLength={1}
            value={v}
            onChange={(e) => onChange(i, e)}
            onKeyDown={(e) => onKeyDown(i, e)}
            aria-label={`Digit ${i + 1}`}
            className={`w-12 h-14 text-center text-[22px] font-semibold text-s360-fg1 bg-white rounded-md outline-none border border-b-2 focus:border-s360-violet focus:border-b-s360-violet ${
              error ? "border-s360-danger border-b-s360-danger" : "border-s360-stroke1"
            }`}
          />
        ))}
      </div>
      {error && (
        <span className="sb-error-text">
          <AlertCircle className="w-3.5 h-3.5" /> {error}
        </span>
      )}
    </div>
  );
};

const ForgotPasswordPage = () => {
  const navigate = useNavigate();
  const [step, setStep] = useState("email"); // email | otp | reset | done

  const [email, setEmail] = useState("");
  const [emailTouched, setEmailTouched] = useState(false);
  const [emailFocus, setEmailFocus] = useState(false);

  const [otp, setOtp] = useState(["", "", "", "", "", ""]);
  const [otpErr, setOtpErr] = useState("");
  const [resendIn, setResendIn] = useState(30);
  const otpRefs = useRef([]);

  const [pw, setPw] = useState("");
  const [pw2, setPw2] = useState("");
  const [pwTouched, setPwTouched] = useState({});
  const [showPw, setShowPw] = useState(false);

  const [submitting, setSubmitting] = useState(false);

  useEffect(() => {
    if (step !== "otp" || resendIn <= 0) return;
    const id = setTimeout(() => setResendIn(resendIn - 1), 1000);
    return () => clearTimeout(id);
  }, [step, resendIn]);

  const emailErr = emailTouched ? validateEmail(email) : "";

  const sendCode = (e) => {
    e?.preventDefault();
    setEmailTouched(true);
    if (validateEmail(email)) return;
    setSubmitting(true);
    setTimeout(() => {
      setSubmitting(false);
      setOtp(["", "", "", "", "", ""]);
      setOtpErr("");
      setResendIn(30);
      setStep("otp");
      setTimeout(() => otpRefs.current[0]?.focus(), 50);
    }, 900);
  };

  const resend = () => {
    if (resendIn > 0) return;
    setOtp(["", "", "", "", "", ""]);
    setOtpErr("");
    setResendIn(30);
    otpRefs.current[0]?.focus();
  };

  const verifyOtp = (e) => {
    e?.preventDefault();
    const code = otp.join("");
    if (code.length < 6) return setOtpErr("Enter the 6-digit code.");
    setSubmitting(true);
    setTimeout(() => {
      setSubmitting(false);
      if (code === "000000") return setOtpErr("That code isn't right. Check it and try again.");
      setStep("reset");
    }, 800);
  };

  const pwErr = pwTouched.pw
    ? !pw
      ? "Enter a new password."
      : pw.length < 8
        ? "Use at least 8 characters."
        : ""
    : "";
  const pw2Err = pwTouched.pw2 ? (pw2 !== pw ? "Passwords don't match." : "") : "";
  const resetValid = pw.length >= 8 && pw2 === pw;

  const resetPassword = (e) => {
    e?.preventDefault();
    setPwTouched({ pw: true, pw2: true });
    if (!resetValid) return;
    setSubmitting(true);
    setTimeout(() => {
      setSubmitting(false);
      setStep("done");
    }, 900);
  };

  return (
    <div className="sb-login-stage sb-dark" data-screen-label="Forgot password">
      <Glyph className="sb-glyph" />

      <div className="sb-shell">
        <div className="sb-brand-lockup">
          <Semi360Logo className="sb-brand-logo" />
        </div>

        {/* STEP 1 — email */}
        {step === "email" && (
          <form className="sb-card" onSubmit={sendCode} noValidate>
            <p className="sb-eyebrow">Buyer portal</p>
            <h1>Reset your password</h1>
            <p className="sb-lede">
              Enter the work email tied to your account and we'll send a 6-digit verification code.
            </p>

            <div className="sb-field">
              <label htmlFor="fp-email">Work email</label>
              <div className={`sb-input-wrap ${emailFocus ? "sb-focused" : ""} ${emailErr ? "sb-error" : ""}`}>
                <span className="sb-lead">
                  <Mail className="w-[18px] h-[18px]" />
                </span>
                <input
                  id="fp-email"
                  type="email"
                  autoComplete="username"
                  placeholder="you@company.com"
                  value={email}
                  onChange={(e) => setEmail(e.target.value)}
                  onFocus={() => setEmailFocus(true)}
                  onBlur={() => {
                    setEmailFocus(false);
                    setEmailTouched(true);
                  }}
                  aria-invalid={!!emailErr}
                />
              </div>
              {emailErr && (
                <span className="sb-error-text">
                  <AlertCircle className="w-3.5 h-3.5" /> {emailErr}
                </span>
              )}
            </div>

            <button type="submit" className="sb-btn sb-btn-primary" disabled={submitting}>
              {submitting ? (
                <>
                  <span className="sb-spinner" /> Sending code…
                </>
              ) : (
                <>
                  Send code <ArrowRight className="w-4 h-4" />
                </>
              )}
            </button>

            <div className="sb-signup-line">
              <button
                type="button"
                className="sb-link inline-flex items-center gap-1.5"
                onClick={() => navigate("/")}
              >
                <ArrowLeft className="w-4 h-4" /> Back to sign in
              </button>
            </div>
          </form>
        )}

        {/* STEP 2 — otp */}
        {step === "otp" && (
          <form className="sb-card" onSubmit={verifyOtp} noValidate>
            <p className="sb-eyebrow">Buyer portal</p>
            <h1>Enter the code</h1>
            <p className="sb-lede">
              We sent a 6-digit code to{" "}
              <strong style={{ color: "var(--sb-fg-1)" }}>{email.trim()}</strong>. It expires in 10
              minutes.
            </p>

            <OtpInput
              otp={otp}
              setOtp={(v) => {
                setOtp(v);
                if (otpErr) setOtpErr("");
              }}
              error={otpErr}
              refs={otpRefs}
            />

            <button type="submit" className="sb-btn sb-btn-primary" disabled={submitting}>
              {submitting ? (
                <>
                  <span className="sb-spinner" /> Verifying…
                </>
              ) : (
                <>
                  Verify <ArrowRight className="w-4 h-4" />
                </>
              )}
            </button>

            <p className="text-[13px] text-center text-s360-fg3 m-0">
              Didn't get the code?{" "}
              {resendIn > 0 ? (
                <span className="text-s360-fg3">Resend in {resendIn}s</span>
              ) : (
                <button type="button" className="sb-link" onClick={resend}>
                  Resend code
                </button>
              )}
            </p>

            <div className="sb-signup-line">
              <button
                type="button"
                className="sb-link inline-flex items-center gap-1.5"
                onClick={() => setStep("email")}
              >
                <ArrowLeft className="w-4 h-4" /> Use a different email
              </button>
            </div>
          </form>
        )}

        {/* STEP 3 — reset */}
        {step === "reset" && (
          <form className="sb-card" onSubmit={resetPassword} noValidate>
            <p className="sb-eyebrow">Buyer portal</p>
            <h1>Set a new password</h1>
            <p className="sb-lede">Choose a strong password you haven't used before.</p>

            <div className="sb-field">
              <label htmlFor="fp-pw">New password</label>
              <div className={`sb-input-wrap ${pwErr ? "sb-error" : ""}`}>
                <span className="sb-lead">
                  <Lock className="w-[18px] h-[18px]" />
                </span>
                <input
                  id="fp-pw"
                  type={showPw ? "text" : "password"}
                  autoComplete="new-password"
                  placeholder="At least 8 characters"
                  value={pw}
                  onChange={(e) => setPw(e.target.value)}
                  onBlur={() => setPwTouched((t) => ({ ...t, pw: true }))}
                  aria-invalid={!!pwErr}
                />
                <button
                  type="button"
                  className="sb-trail"
                  onClick={() => setShowPw((s) => !s)}
                  aria-label={showPw ? "Hide password" : "Show password"}
                  tabIndex={-1}
                >
                  {showPw ? <EyeOff className="w-[15px] h-[15px]" /> : <Eye className="w-[15px] h-[15px]" />}
                </button>
              </div>
              {pwErr && (
                <span className="sb-error-text">
                  <AlertCircle className="w-3.5 h-3.5" /> {pwErr}
                </span>
              )}
            </div>

            <div className="sb-field">
              <label htmlFor="fp-pw2">Confirm password</label>
              <div className={`sb-input-wrap ${pw2Err ? "sb-error" : ""}`}>
                <span className="sb-lead">
                  <Lock className="w-[18px] h-[18px]" />
                </span>
                <input
                  id="fp-pw2"
                  type={showPw ? "text" : "password"}
                  autoComplete="new-password"
                  placeholder="Re-enter your password"
                  value={pw2}
                  onChange={(e) => setPw2(e.target.value)}
                  onBlur={() => setPwTouched((t) => ({ ...t, pw2: true }))}
                  aria-invalid={!!pw2Err}
                />
              </div>
              {pw2Err && (
                <span className="sb-error-text">
                  <AlertCircle className="w-3.5 h-3.5" /> {pw2Err}
                </span>
              )}
            </div>

            <button type="submit" className="sb-btn sb-btn-primary" disabled={submitting}>
              {submitting ? (
                <>
                  <span className="sb-spinner" /> Updating…
                </>
              ) : (
                <>
                  Reset password <ArrowRight className="w-4 h-4" />
                </>
              )}
            </button>
          </form>
        )}

        {/* STEP 4 — done */}
        {step === "done" && (
          <div className="sb-card">
            <span className="w-12 h-12 rounded-full bg-s360-success-bg text-s360-success inline-flex items-center justify-center self-start">
              <ShieldCheck className="w-6 h-6" />
            </span>
            <h1>Password updated</h1>
            <p className="sb-lede">
              Your password has been changed. Use it next time you sign in to semi360.
            </p>
            <button
              type="button"
              className="sb-btn sb-btn-primary"
              onClick={() => navigate("/")}
            >
              Back to sign in <ArrowRight className="w-4 h-4" />
            </button>
          </div>
        )}
      </div>
    </div>
  );
};

export default ForgotPasswordPage;
